import React from 'react'
import { CircularProgress } from '@mui/material'
import { api } from '../../function/axios'
import { UsageStatsDialogV2, UsageStat } from './UsageStatsDialogV2'
import { useDispatch } from '../../store'
import { calendarRangeSearch } from '../../store/user/actionCreator'
import { Subscriber } from '../../store/user/reducer'
import s from './ActivityCalendarV2.module.css'

/* the day's shade — translucent green, from the lightest (a quiet day) to the deepest (the month's busiest) */
const HEAT = [s.heat0, s.heat1, s.heat2, s.heat3]

/* a past day is crossed out like a paper calendar — a translucent red marker
   X under the number, two strokes, the same on every day */
const CROSS: [string, string] = ['M2 4 L28 17', 'M28 3 L3 18']
function Cross() {
  const [a, b] = CROSS
  return (
    <svg className={s.cross} viewBox="0 0 30 20" preserveAspectRatio="none" aria-hidden>
      <path d={a} pathLength={1} vectorEffect="non-scaling-stroke" />
      <path d={b} pathLength={1} vectorEffect="non-scaling-stroke" />
    </svg>
  )
}

// Same behaviour as the old CommonCalendar: month grid, per-day request
// badges from /admin-dashboard/common-calendar, click two days for a From/To
// range, then "Show stats" loads that range into UsageStatsDialogV2 (the v2
// version of the old UsageStatsAll popup, same data).

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
const FIRST_YEAR = 2021

const ymd = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

// Badge shade by volume, relative to the busiest day of the visible month.

/*
 * With `subscriber` (subscriber_v2's Activity calendar tab) the same calendar
 * shows one user, exactly as the classic tab (component/calendar/Calendar.tsx)
 * did: the day badges come from the subscriber's own usage calendar, already
 * loaded with the subscriber, and "Show stats" asks
 * /admin-dashboard/calendar-search/:id for the range.
 */
type Props = { subscriber?: Subscriber }

/* calendar-search answers per day with {date, count, requests:[{type, count}]} — the dialog's shape, for one user */
function subscriberStats(data: any, userId: number): UsageStat[] {
  const days: any[] = Array.isArray(data) ? data : []
  return days.map((d) => {
    const requests = (Array.isArray(d?.requests) ? d.requests : []).map((r: any) => ({
      type: String(r?.type ?? ''), count: Number(r?.count) || 0, userId,
    }))
    return {
      date: String(d?.date ?? ''),
      count: Number(d?.count) || 0,
      users_count: 1,
      users: [userId],
      types_present: Array.from(new Set(requests.map((r: any) => r.type))) as string[],
      web_requests: Number(d?.web_requests) || 0,
      api_requests: Number(d?.api_requests) || 0,
      requests,
      requests_by_user: [{ count: Number(d?.count) || 0, userId }],
    }
  })
}

export function ActivityCalendarV2({ subscriber }: Props = {}) {
  const dispatch = useDispatch()
  const today = React.useMemo(() => new Date(), [])
  const todayKey = ymd(today)
  const [view, setView] = React.useState(() => new Date(today.getFullYear(), today.getMonth(), 1))
  const [counts, setCounts] = React.useState<Record<string, number>>({})
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [from, setFrom] = React.useState<string | null>(null)
  const [to, setTo] = React.useState<string | null>(null)
  const [rangeTotal, setRangeTotal] = React.useState<number | null>(null)
  const [stats, setStats] = React.useState<any[] | null>(null)
  const [statsLoading, setStatsLoading] = React.useState(false)

  const year = view.getFullYear()
  const month = view.getMonth()
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const startDay = (new Date(year, month, 1).getDay() + 6) % 7

  // one subscriber: every day is already in hand, loaded with the subscriber
  const ownCounts = React.useMemo<Record<string, number> | null>(() => {
    if (!subscriber) return null
    const list: any[] = (subscriber as any)?.calendar?.data ?? []
    return Object.fromEntries(list.map((item) => [item?.date, Number(item?.count) || 0]))
  }, [subscriber])

  React.useEffect(() => {
    if (ownCounts) {
      setCounts(ownCounts)
      return undefined
    }
    let cancelled = false
    setLoading(true)
    setError(null)
    api({
      method: 'GET',
      url: '/admin-dashboard/common-calendar',
      params: { start: ymd(new Date(year, month, 1)), end: ymd(new Date(year, month + 1, 0)) },
    })
      .then(({ data }) => {
        if (cancelled) return
        const list: any[] = Array.isArray(data) ? data : data?.calendar ?? []
        setCounts(Object.fromEntries(list.map((item) => [item?.date, Number(item?.count) || 0])))
      })
      .catch((e) => {
        if (!cancelled) setError(e?.message || 'Failed to load')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [year, month, ownCounts])

  // Range total comes from the same endpoint, so it's right even when the
  // range spans months that aren't on screen.
  React.useEffect(() => {
    setRangeTotal(null)
    if (!from || !to) return
    if (ownCounts) {
      setRangeTotal(Object.entries(ownCounts).reduce((sum, [d, n]) => (d >= from && d <= to ? sum + n : sum), 0))
      return
    }
    let cancelled = false
    api({ method: 'GET', url: '/admin-dashboard/common-calendar', params: { start: from, end: to } })
      .then(({ data }) => {
        if (cancelled) return
        const list: any[] = Array.isArray(data) ? data : data?.calendar ?? []
        setRangeTotal(list.reduce((sum, item) => sum + (Number(item?.count) || 0), 0))
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [from, to, ownCounts])

  // the subscriber's counts cover every month — only the visible one counts here
  const monthPrefix = `${year}-${String(month + 1).padStart(2, '0')}-`
  const monthValues = Object.entries(counts).filter(([d]) => d.startsWith(monthPrefix)).map(([, n]) => n)
  const monthMax = Math.max(0, ...monthValues)
  const monthTotal = monthValues.reduce((a, b) => a + b, 0)
  const level = (n: number) => {
    if (!monthMax) return 0
    const r = n / monthMax
    return r < 0.25 ? 0 : r < 0.5 ? 1 : r < 0.8 ? 2 : 3
  }

  const pick = (key: string) => {
    if (!from || (from && to)) {
      setFrom(key)
      setTo(null)
    } else if (key < from) {
      setTo(from)
      setFrom(key)
    } else {
      setTo(key)
    }
  }

  const go = (y: number, m: number) => setView(new Date(y, m, 1))

  const showStats = () => {
    if (!from || !to) return
    setStatsLoading(true)
    if (subscriber) {
      dispatch(calendarRangeSearch(subscriber.id, from, to, (data: any) => {
        setStats(subscriberStats(data, subscriber.id))
        setStatsLoading(false)
      }))
      // a failed request never calls back — the button must not stay stuck
      setTimeout(() => setStatsLoading(false), 15000)
      return
    }
    api({ method: 'GET', url: '/admin-dashboard/common-calendar', params: { start: from, end: to } })
      .then(({ data }) => setStats(Array.isArray(data) ? data : data?.calendar ?? []))
      .finally(() => setStatsLoading(false))
  }

  const years: number[] = []
  for (let y = FIRST_YEAR; y <= today.getFullYear(); y++) years.push(y)

  return (
    <div className={s.root}>
      <div className={s.head}>
        <h3>Search activity</h3>
        <span className={s.hint}>· {subscriber ? 'this subscriber' : 'all users'} · click two days to select a range</span>
      </div>

      <div className={s.body}>
        <div className={s.controls}>
          <button className={s.nav} onClick={() => go(year, month - 1)} aria-label="Previous month">‹</button>
          <select className={s.select} value={month} onChange={(e) => go(year, Number(e.target.value))}>
            {MONTHS.map((m, i) => <option key={m} value={i}>{m}</option>)}
          </select>
          <select className={s.select} value={year} onChange={(e) => go(Number(e.target.value), month)}>
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <button className={s.nav} onClick={() => go(year, month + 1)} aria-label="Next month">›</button>
          <button className={s.today} onClick={() => go(today.getFullYear(), today.getMonth())}>Today</button>
          <span className={s.monthTotal}>
            Month total: <b>{monthTotal.toLocaleString()}</b> requests
          </span>
          <span className={s.legend} aria-hidden>
            Less{HEAT.map((h) => <i key={h} className={h} />)}More
          </span>
        </div>

        <div className={s.grid}>
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => <div key={d} className={s.dow}>{d}</div>)}
          {Array.from({ length: startDay }, (_, i) => <div key={`e${i}`} className={`${s.day} ${s.empty}`} />)}
          {Array.from({ length: daysInMonth }, (_, i) => {
            const date = new Date(year, month, i + 1)
            const key = ymd(date)
            const future = key > todayKey
            const n = counts[key] || 0
            const weekend = (date.getDay() + 6) % 7 > 4
            const isEdge = key === from || key === to
            const inRange = !!(from && to && key > from && key < to)
            const cls = [
              s.day,
              weekend ? s.weekend : '',
              key === todayKey ? s.todayCell : '',
              future ? s.future : '',
              // a heat map: the busier the day against the month's busiest, the deeper the cell
              n > 0 ? HEAT[level(n)] : '',
              isEdge ? s.edge : '',
              inRange ? s.inRange : '',
            ].join(' ')
            return (
              <button key={key} type="button" className={cls} disabled={future} onClick={() => pick(key)} title={n ? `${n.toLocaleString()} requests` : undefined}>
                <span className={s.num}>{key < todayKey && <Cross />}<i>{i + 1}</i></span>
                {n > 0 && <span className={s.cnt}><b>{n.toLocaleString()}</b><small>req</small></span>}
              </button>
            )
          })}
          {loading && (
            <div className={s.loading}><CircularProgress size={26} /></div>
          )}
        </div>
        {error && <div className={s.error}>{error}</div>}
      </div>

      <div className={s.foot}>
        <div className={s.range}>
          <span>From: <b>{from || '—'}</b></span>
          <span>To: <b>{to || '—'}</b></span>
          <span>Requests: <b>{rangeTotal !== null ? rangeTotal.toLocaleString() : '—'}</b></span>
        </div>
        <span className={s.spacer} />
        <button className={`${s.btn} ${s.ghost}`} onClick={() => { setFrom(null); setTo(null) }} disabled={!from}>Clear</button>
        <button className={s.btn} onClick={showStats} disabled={!from || !to || statsLoading}>
          {statsLoading ? 'Loading…' : 'Show stats'}
        </button>
      </div>

      {stats && from && to && <UsageStatsDialogV2 data={stats} from={from} to={to} onClose={() => setStats(null)} />}
    </div>
  )
}
